import React from 'react';
import { ArrowRight } from 'lucide-react';
import { IS_APP_BUILD } from '../lib/buildTarget';
import {
  ofertasDeSeries7, ofertaAcentuada, tituloDeOferta, precioWebDeOferta, renglonDeEspacioDeOferta,
} from '../lib/series7';
import { precioDeOfertaEnTienda, useCatalogoDeLaTienda } from '../lib/precioDeOferta';

/**
 * Server-resolved upsell cards, shared by AutoSplitModal and the /pending page.
 * Renders affordability.suggestions exactly as the server computed them (cheap→expensive,
 * the strongest result accented). No business math here.
 */
const PLAN_LABEL = { pro: 'Pro' };

// ── TANDA L / L3.6 (censo #32, firma de Carlos 2026-08-08): MUERE "LAS 1 PARTE JUNTAS" ──
//
// Estas dos frases se escribieron para el modal de SUBIDA, donde `n` es el número de partes
// en que se acaba de partir un documento y por construcción vale 2 o más. Pero la MISMA
// tarjeta se reusa en Pendientes, y ahí `n` es cuántas partes FALTAN — que muy a menudo es
// una. Con n=1, `{{parts}}` se resuelve a "1 parte" y la frase queda:
//     "Te falta 1 crédito para procesar las 1 parte juntas"   (ES)
//     "You're 1 credit short of all 1 part at once"           (EN)
// Es el último renglón que un usuario lee antes de decidir si paga.
//
// NO SE PUEDE ARREGLAR CON EL PLURAL DE i18next: esa frase ya está pluralizada por
// `{{count}}` —los CRÉDITOS— y una clave no puede pluralizar por dos cantidades a la vez.
// Por eso el singular de PARTES es una clave propia, elegida aquí. El plural de créditos
// sigue funcionando dentro de ella (`_single_one` / `_single_other`).
const esUnaSolaParte = (n) => n === 1;

export function suggestionCardCopy(t, c, n) {
  const plan = PLAN_LABEL[c.instrument];
  const partsAmt = (count) => t('dashboard:auto_split.parts_amount', { count });
  const sola = esUnaSolaParte(n);
  if (c.instrument === 'pack') {
    return c.result === 'all_now'
      ? { title: t('dashboard:auto_split.card_pack_allnow_title'),
          sub: sola
            ? t('dashboard:auto_split.card_pack_allnow_sub_single', { count: c.credits })
            : t('dashboard:auto_split.card_pack_allnow_sub', { count: c.credits, parts: partsAmt(n) }) }
      : { title: t('dashboard:auto_split.card_pack_enters_title'),
          sub: t('dashboard:auto_split.card_pack_enters_sub', { kp: partsAmt(c.parts_now), rp: partsAmt(c.parts_saved) }) };
  }
  return c.result === 'all_now'
    ? { title: t('dashboard:auto_split.card_plan_allnow_title', { plan }),
        sub: sola
          ? t('dashboard:auto_split.card_plan_allnow_sub_single')
          : t('dashboard:auto_split.card_plan_allnow_sub', { parts: partsAmt(n) }) }
    : { title: t('dashboard:auto_split.card_plan_enters_title', { plan }),
        sub: t('dashboard:auto_split.card_plan_enters_sub', { kp: partsAmt(c.parts_now), rp: partsAmt(c.parts_saved) }) };
}

// -- series-7 (bloque A, #489, 24-sep-2026): LAS OFERTAS DE `series7` --
//
// Cuando el sobre trae `series7`, las tarjetas son sus `offers`, EN EL ORDEN DEL SERVIDOR
// (ya vienen escogidas: el paso natural primero y la combinacion mas barata que termina
// despues; reordenarlas por precio las volveria a mezclar). Mismo cuadro, mismo boton y
// las mismas clases que la tarjeta de hoy; lo que cambia es lo que dicen:
//   - el titulo es U8 (U7 cuando la combinacion es Pro sola);
//   - la tarjeta de hoy NO tiene un elemento de precio (el precio solo servia para
//     ordenar), asi que el precio va en el renglon del subtitulo, con sus clases: en web
//     el total que manda el servidor con el formato de CreditPackModal ("$17.98"); en la
//     app la suma de los precios de Apple de ESOS productos (lib/precioDeOferta.js), y sin
//     cifra si falta alguno (mientras el catalogo carga, un renglon en blanco para que la
//     tarjeta no brinque);
//   - si aun comprando algunas partes esperan espacio, un tercer renglon con U12.
// Sin `series7` todo lo de abajo es lo de hoy, byte a byte (AutoSplitModal.s7paridad).
const ESPACIO_EN_BLANCO = '\u00a0';

function precioDeLaOferta(oferta, catalogo, idioma) {
  if (!IS_APP_BUILD) return precioWebDeOferta(oferta);
  const p = precioDeOfertaEnTienda(oferta, catalogo, idioma);
  if (p) return p;
  const cargando = !catalogo || catalogo.status === 'idle' || catalogo.status === 'loading';
  return cargando ? ESPACIO_EN_BLANCO : null;
}

/* pantallas-alumno (G), parte 2 "Oferta de compra" (pieza propia de Pendientes): renglon de 60, fondo #101010, borde
   #262626, radio 14; titulo 14.5 seminegrita, precio 13 #9A9A9A y flecha naranja; la recomendada (la misma de hoy)
   con borde #F27A2E y fondo #17100B. P6 (firma del 19-oct): con `apagado` (sin red) se ven al 38 % (seccion 4.1
   del encargo) y el toque llama a
   `alTocarApagado` ("You need a connection") en lugar de comprar. Sin `estilo` ni `apagado`, lo de siempre (la
   ventana del documento grande del Panel), clase por clase. */
const OFERTA_ALUMNO = 'relative w-full text-left rounded-[14px] px-4 py-2.5 min-h-[60px] flex items-center gap-3 border focus:outline-none focus-visible:ring-2 focus-visible:ring-white';
const OFERTA_ALUMNO_ACENTO = 'border-[#F27A2E] bg-[#17100B]';
const OFERTA_ALUMNO_NORMAL = 'border-[#262626] bg-[#101010] hoverable:border-[#3A2414]';

export default function AutoSplitSuggestionCards({ t, suggestions, n, onSelect, testidPrefix = 'auto-split', series7, idioma, estilo, apagado = false, alTocarApagado }) {
  const alumno = estilo === 'alumno';
  const tocar = (oferta) => {
    if (apagado) { if (alTocarApagado) alTocarApagado(); return; }
    onSelect(oferta);
  };
  const extra = apagado ? { 'aria-disabled': 'true', style: { opacity: 0.38 } } : {};
  const claseTitulo = alumno ? 'text-[14.5px] leading-5 font-semibold text-[#F4F4F3] break-words' : 'text-sm font-semibold text-white break-words';
  const claseRenglon = alumno ? 'text-[13px] text-[#9A9A9A] break-words mt-0.5' : 'text-xs text-[#a0a0a0] break-words mt-0.5';
  const ofertas = series7 ? ofertasDeSeries7(series7) : null;
  const catalogo = useCatalogoDeLaTienda(!!(ofertas && ofertas.length));
  if (ofertas) {
    if (!ofertas.length) return null;
    const acento = ofertaAcentuada(ofertas);
    return (
      <div className="space-y-2" data-testid={`${testidPrefix}-suggestions`}>
        {ofertas.map((o, i) => {
          const precio = precioDeLaOferta(o, catalogo, idioma);
          const espacio = renglonDeEspacioDeOferta(t, o);
          return (
            <button
              key={`oferta-${i}`}
              onClick={() => tocar(o)}
              {...extra}
              className={alumno ? `${OFERTA_ALUMNO} ${i === acento ? OFERTA_ALUMNO_ACENTO : OFERTA_ALUMNO_NORMAL}` : `w-full text-left rounded-xl px-4 py-3 transition-colors min-h-[44px] flex items-center gap-3 ${
                i === acento ? 'bg-[#1a1a1a] border-2 border-[#FF6B00]' : 'bg-[#1a1a1a] border border-[#262626] hover:border-[#FF6B00]/40'
              }`}
              data-testid={`${testidPrefix}-oferta-${i}`}
            >
              <div className="flex-1 min-w-0">
                <div className={claseTitulo}>{tituloDeOferta(t, o)}</div>
                {precio !== null && (
                  <div className={claseRenglon} data-testid={`${testidPrefix}-oferta-${i}-precio`}>{precio}</div>
                )}
                {espacio && (
                  <div className={claseRenglon} data-testid={`${testidPrefix}-oferta-${i}-espacio`}>{espacio}</div>
                )}
              </div>
              <ArrowRight className={alumno ? 'w-4 h-4 text-[#F27A2E] shrink-0' : 'w-4 h-4 text-[#FF6B00] shrink-0'} />
            </button>
          );
        })}
      </div>
    );
  }
  const cards = [...(suggestions || [])].sort((a, b) => (a.price_usd || 0) - (b.price_usd || 0));
  if (!cards.length) return null;
  const accentIdx = (() => {
    const i = cards.findIndex(c => c.result === 'all_now');
    return i >= 0 ? i : 0;
  })();
  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-suggestions`}>
      {cards.map((c, i) => {
        const { title, sub } = suggestionCardCopy(t, c, n);
        const accent = i === accentIdx;
        return (
          <button
            key={`${c.instrument}-${i}`}
            onClick={() => tocar(c)}
            {...extra}
            className={alumno ? `${OFERTA_ALUMNO} ${accent ? OFERTA_ALUMNO_ACENTO : OFERTA_ALUMNO_NORMAL}` : `w-full text-left rounded-xl px-4 py-3 transition-colors min-h-[44px] flex items-center gap-3 ${
              accent ? 'bg-[#1a1a1a] border-2 border-[#FF6B00]' : 'bg-[#1a1a1a] border border-[#262626] hover:border-[#FF6B00]/40'
            }`}
            data-testid={`${testidPrefix}-card-${c.instrument}-${c.result}`}
          >
            <div className="flex-1 min-w-0">
              <div className={claseTitulo}>{title}</div>
              <div className={claseRenglon}>{sub}</div>
            </div>
            <ArrowRight className={alumno ? 'w-4 h-4 text-[#F27A2E] shrink-0' : 'w-4 h-4 text-[#FF6B00] shrink-0'} />
          </button>
        );
      })}
    </div>
  );
}
